
  import {
    defineComponent, nextTick,
    onMounted,
    PropType,
    reactive,
    toRefs,
    watch
  } from "vue";
  import Customer from "@/model/Customer";
  import CompanyLocationService, {CompanyLocationV2Dto} from "@/service/CompanyLocationService";
  import {CustomerTravelTime} from "@/model/CustomerTravelTime";
  import CustomerTravelTimeService from "@/service/CustomerTravelTimeService";
  import ModalDialog from "@/components/ModalDialog.vue";
  import CustomerDetailsCardListEntry from "@/views/Customers/details/CustomerDetailsCardListEntry.vue";

  class ListEntry {
    public label: string;
    public text: string;
  }

  class TravelTimeModel {
    public companyLocation: CompanyLocationV2Dto;
    public hours: number;
    public minutes: number;
  }

  export default defineComponent({
    name: "CustomerDetailsCardTravelTimes",
    components: {
      CustomerDetailsCardListEntry,
      ModalDialog
    },
    props: {
      editable: {
        type: Boolean,
        default: false
      },
      customer: {
        type: Object as PropType<Customer>,
        default: () => new Customer()
      }
    },
    setup(props, context) {
      const initialState = {
        dialog: false,
        companyLocations: new Array<CompanyLocationV2Dto>(),
        customerTravelTimes: new Array<CustomerTravelTime>(),
        travelTimeModels: new Array<TravelTimeModel>(),
        listEntries: [] as Array<ListEntry>
      };

      const state = reactive({...initialState});

      onMounted(() => {
        loadCompanyLocations();
      });

      watch(() => props.customer, () => {
        loadCompanyLocations();
      });

      const loadCompanyLocations = () => {
        if (props.customer.uuid) {
          CompanyLocationService.findCompanyLocations()
            .then(companyLocations => {
              state.companyLocations = companyLocations;
            });
        }
      };

      watch(() => state.companyLocations, () => {
        loadCustomerTravelTimes();
      });

      const loadCustomerTravelTimes = () => {
        if (!props.customer.uuid) {
          return;
        }
        CustomerTravelTimeService.getCustomerTravelTimes(props.customer.uuid)
          .then(response => {
            state.customerTravelTimes = response.data.travelTimes;
          });
      };

      watch(() => state.customerTravelTimes, () => {
        updateTravelTimeModels();
      },{ deep: true });

      const updateTravelTimeModels = () => {
        state.travelTimeModels = new Array<TravelTimeModel>();
        state.companyLocations.forEach(location => {
          const customerTravelTime = state.customerTravelTimes.find(time => time.companyLocationId == location.id);
          state.travelTimeModels.push({
            companyLocation: location,
            hours: customerTravelTime ? Math.floor(customerTravelTime.minutes / 60) : 0,
            minutes: customerTravelTime ? customerTravelTime.minutes % 60 : 0,
          });
        });
      };

      watch(() => state.travelTimeModels, () => {
        updateListEntries(state.companyLocations);
      });

      const updateListEntries = (companyLocations: CompanyLocationV2Dto[]) => {
        state.listEntries = companyLocations.map(companyLocation => {
          let text = "–";
          const model = state.travelTimeModels.find(m => m.companyLocation.id == companyLocation.id);
          if (model) {
            if (model.hours > 0 || model.minutes > 0) {
              const h = model.hours.toString().length == 1 ? `0${model.hours}` : model.hours;
              const m = model.minutes.toString().length == 1 ? `0${model.minutes}` : model.minutes;
              text = `${h}:${m}`;
            }
          }
          return {
            label: companyLocation.name,
            text
          };
        });
      };

      const click = () => {
        if (state.listEntries.length > 0) {
          updateTravelTimeModels();
          state.dialog = true;
        } else {
          state.dialog = false;
        }
      };

      const inputHours = (hours: string, companyLocationId: string) => {
        let hoursInt = parseInt(hours.replace (/[^0-9]/g, "" ));
        if (hoursInt > 99) hoursInt = 99;
        nextTick(() => {
          const travelTimeModel = state.travelTimeModels.find(m => m.companyLocation.id == companyLocationId);
          if (travelTimeModel) {
            travelTimeModel.hours = isNaN(hoursInt) ? 0 : hoursInt;
          }
        });
      };
      const inputMinutes = (minutes: string, companyLocationId: string) => {
        let minutesInt = parseInt(minutes.replace (/[^0-9]/g, "" ));
        if (minutesInt > 59) minutesInt = 59;
        nextTick(() => {
          const travelTimeModel = state.travelTimeModels.find(m => m.companyLocation.id == companyLocationId);
          if (travelTimeModel) {
            travelTimeModel.minutes = isNaN(minutesInt) ? 0 : minutesInt;
          }
        });
      };

      const save = () => {
        if (!props.customer.uuid) {
          return;
        }
        const travelTimes: CustomerTravelTime[] = state.travelTimeModels
          .map(travelTimeModel => ({
            companyLocationId: travelTimeModel.companyLocation.id,
            minutes: travelTimeModel.hours * 60 + travelTimeModel.minutes
          }))
          .filter(travelTime => travelTime.minutes > 0);

        CustomerTravelTimeService.updateCustomerTravelTimes(props.customer.uuid, travelTimes)
          .then(response => {
            state.customerTravelTimes = response.data.travelTimes;
            state.dialog = false;
          })
          .catch(() => { });
      };

      return {
        ...toRefs(state),
        click,
        inputHours,
        inputMinutes,
        save
      };
    }
  });
